import React, { useEffect, useState } from "react";
import { Card, Col, Nav, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useTranslation } from "react-i18next";
import { UserService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { useParams } from "react-router-dom";

interface IProps {
  selectedFile?: File | null;
  onFileSelect?: (file: File) => void;
}

const UserInfo = ({ selectedFile, onFileSelect }: IProps) => {
  const { userData } = useSelector((state: RootState) => state?.auth);
  const { t } = useTranslation();
  const { userId } = useParams();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageKey, setImageKey] = useState(0);

  // Fetch user image on component mount and when userId changes
  useEffect(() => {
    const id = userId || userData?.id;
    if (!id) return;

    // Cleanup previous blob URL to prevent memory leaks
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    UserService.getImageBlob(Number(id), 90, 90)
      .then((blobUrl) => {
        console.log("Image blob URL:", blobUrl);
        setImageUrl(blobUrl);
      })
      .catch((err) => {
        console.error("Failed to load image:", err);
        setImageUrl(null);
      });

    // Cleanup on unmount
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [userId, userData?.id, imageKey]);

  const handleFileSelect = (file: File) => {
    if (onFileSelect) {
      // Store file for form submission (both new and existing users)
      onFileSelect(file);
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(previewUrl);
  };

  return (
    <React.Fragment>
      <Col lg={5} xxl={3}>
        <Card className="overflow-hidden">
          <Card.Body className="position-relative">
            <div className="text-center mt-3">
              <div className="chat-avtar d-inline-flex mx-auto mb-2 position-relative">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={`${userData?.firstName} ${userData?.lastName}`}
                    className="rounded-circle img-fluid wid-90 img-thumbnail hei-90"
                    style={{ objectFit: 'cover' }}
                    onError={() => {
                      console.error("Image failed to load");
                      setImageUrl(null);
                    }}
                  />
                ) : (
                  <div className="rounded-circle d-flex align-items-center justify-content-center bg-light-primary wid-90 img-thumbnail hei-90" style={{ backgroundColor: '#e7f0ff' }}>
                    <i className="ph-duotone ph-user text-primary" style={{ fontSize: '3rem' }}></i>
                  </div>
                )}

                {/* Hidden file input */}
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/*"
                  className="d-none"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    handleFileSelect(file);
                  }}
                />

                <label
                  htmlFor="avatar-upload"
                  className="position-absolute cursor-pointer bottom-0 end-0 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center cursor-pointer"
                  style={{ width: 32, height: 32 }}
                >
                  <i className="ph-duotone ph-pencil-simple"></i>
                </label>
              </div>

              <h5 className="mb-0">
                {userData?.firstName} {userData?.lastName}
              </h5>
              <p className="text-muted text-sm">{userData?.institutionName}</p>
              <h5 className="mb-0">86</h5>
              <small className="text-muted">Number of incidents</small>
            </div>
          </Card.Body>
          <Nav
            variant="pills"
            className="flex-column list-group list-group-flush account-pills mb-0"
            id="user-set-tab"
            role="tablist"
            aria-orientation="vertical"
          >
            <Nav.Link
              eventKey="PersonalInfo"
              className="list-group-item list-group-item-action"
              id="user-set-information-tab"
            >
              <span className="f-w-500">
                <i className="ph-duotone ph-clipboard-text m-r-10"></i>
                {t("general.personalInformation")}
              </span>
            </Nav.Link>
            {userId && (
              <Nav.Link
                eventKey="ChangePassword"
                className="list-group-item list-group-item-action"
                id="user-set-passwort-tab"
              >
                <span className="f-w-500">
                  <i className="ph-duotone ph-key m-r-10"></i>
                  {t("general.changePassword")}
                </span>
              </Nav.Link>
            )}
          </Nav>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default UserInfo;
